import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";

const STAR_EMOJI = ["✨", "⭐", "🌟", "💫", "✦", "☆", "★"] as const;
const MIN_MS = 10_000;

type Props = {
  active: boolean;
};

export function AstralAutoGatherOverlay({ active }: Props) {
  const [progress, setProgress] = useState(0);
  const stars = useMemo(
    () =>
      Array.from({ length: 28 }, (_, i) => ({
        id: i,
        emoji: STAR_EMOJI[i % STAR_EMOJI.length],
        left: `${(i * 19 + 5) % 96}%`,
        top: `${(i * 27 + 9) % 88}%`,
        delay: (i * 137) % 900,
        duration: 800 + ((i * 53) % 1200),
      })),
    [],
  );

  useEffect(() => {
    if (!active) {
      setProgress(0);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const elapsed = now - start;
      setProgress(Math.min(1, elapsed / MIN_MS));
      if (elapsed < MIN_MS) {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  if (!active) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-sm rounded-2xl border border-indigo-400/40 bg-gradient-to-b from-indigo-950 via-slate-950 to-black p-6 overflow-hidden shadow-[0_0_40px_rgba(99,102,241,0.35)]">
        <div className="absolute inset-0 pointer-events-none">
          {stars.map((s) => (
            <span
              key={s.id}
              className="absolute text-lg animate-pulse"
              style={{
                left: s.left,
                top: s.top,
                animationDuration: `${s.duration}ms`,
                animationDelay: `${s.delay}ms`,
                opacity: 0.35 + (s.id % 5) * 0.12,
              }}
            >
              {s.emoji}
            </span>
          ))}
        </div>
        <div className="relative z-10 space-y-4 text-center">
          <p className="text-sm font-semibold tracking-wide text-indigo-200 uppercase">
            Auto astral gather
          </p>
          <p className="text-xs text-gray-400 leading-relaxed">
            Channeling starlight — your gold is spent in rapid gathers until the budget runs out or your
            bag is full.
          </p>
          <div className="h-2.5 rounded-full bg-indigo-950 border border-indigo-500/30 overflow-hidden">
            <div
              className={clsx(
                "h-full rounded-full bg-gradient-to-r from-violet-500 via-indigo-400 to-sky-400 transition-[width] duration-150",
              )}
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
          <p className="text-[11px] text-gray-500">{progress < 1 ? "Gathering…" : "Finishing…"}</p>
        </div>
      </div>
    </div>
  );
}
